import React, { useCallback, useState, useEffect, FC, useRef } from 'react';
import styled, { css } from 'styled-components';
import Scrollbars from 'react-custom-scrollbars-2';
import { rgba } from 'polished';
import { faEllipsisH } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';

import { useDropdown } from '../../../tools/hooks';
import { useEscKey } from '../../../tools/keyboardTools';
import { colorStack } from '../../../styleHelpers/colors';
import { fontSize, fontSizeAndHeight } from '../../../styleHelpers/fontSizes';
import boxShadow, { modalsShadow } from '../../../styleHelpers/mixins/shadow';
import { Button, EButtonTypeSchema } from '../Buttons/NewButton';
import { SingleStep } from './SingleStep';
import { ETemplateTypes } from '../../../entities/IClusters';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import IntlMessage, { useIntlMessage } from '../IntlMessage';
import { ConfirmPopup } from '../ConfirmPopup/ConfirmPopup';
import { LoadingStep } from './LoadingStep';

export enum EPopupSizes {
    XSMALL = 'XSMALL',
    SMALL = 'SMALL',
    MEDIUM = 'MEDIUM',
    BIG = 'BIG',
    MONSTER = 'MONSTER',
    FULL = 'FULL'
}

const SWrapper = styled.div`
    position: fixed;
    width: 100%;
    height: 100%;
    background: ${rgba(colorStack.label, .75)};
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 99999;
    ${modalsShadow()};
`;

const RightMenuWrapper = styled.div`
    display: flex;
`;

const SWrapperInner = styled.div<{ size: EPopupSizes }>`
    background: ${colorStack.white};
    font-family: 'Roboto', sans-serif;
    letter-spacing: 0.5px;
    border-radius: 8px;
    ${boxShadow()};
    max-width: 100%;
    ${props => props.size === EPopupSizes.XSMALL && css`
        width: auto;
        max-width: 800px;
    `}
    ${props => props.size === EPopupSizes.SMALL && css`
        width: 800px;
    `}
    ${props => props.size === EPopupSizes.MEDIUM && css`
        width: 900px;
    `}
    ${props => props.size === EPopupSizes.BIG && css`
        width: 1024px;
    `}
    ${props => props.size === EPopupSizes.MONSTER && css`
        width: 1200px;
    `}
`;

const ContextMenuWrapper = styled.div`
    position: relative;
    > svg {
        color: ${colorStack.darkBlue};
        margin: 0 1rem 0 0;
        font-size: ${fontSize[20]};
        cursor: pointer;
    }
`;

const SHeader = styled.div`
    padding: 1.5rem;
    border-radius: 8px 8px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: ${colorStack.whiteGrey};
    svg {
        fill: ${colorStack.disabled};
        &:hover{
            fill: ${colorStack.red}
        }
    }
`;
const STitle = styled.div`
    font-weight: 700;
    color: ${colorStack.content};
    ${fontSizeAndHeight[20]};
`;
const SClosePopup = styled.div`
    cursor: pointer;
    svg {
        font-size: 24px;
    }
`;

const SContent = styled.div`
    height: 60vh;
    position: relative;
`;

const SpinnerWrapper = styled.div`
    display: flex;
    align-items: center;
`;
const SFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    border-radius: 0 0 8px 8px;
`;
const SSteps = styled.div`
    width:55%;
    ${fontSizeAndHeight[16]};
    color: ${colorStack.content};
    span {
        margin-left: 24px;
        ${fontSizeAndHeight[13]};
        color: ${colorStack.middleGrey};
    }
`;
const SButtonBox = styled.div`
    display: flex;
    justify-content: space-between;
    gap: .5rem;
`;

const OustideContentWrapper = styled.div<{ isHidden: boolean }>`
    ${props => props.isHidden && css`
        display: hidden;
    `}
`;

const SStepsContentWrapper = styled.div<{ isHidden: boolean }>`
    height: 100%;
    ${props => props.isHidden && css`
        visibility: hidden;
    `}
`;

const SScrollbarView = styled.div`
    scrollbar-width: thin;
    scrollbar-color: ${colorStack.middleGrey} transparent;
    &::-webkit-scrollbar {
        width: 6px;
        background-color: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background-color: ${colorStack.middleGrey};
        border-radius: 3px;
    }
`;

const SLoadingStepOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: ${colorStack.white};
    z-index: 2;
`;

export interface ILoadingStepConfig {
    intervalMs: number;
    texts: string[];
}

export interface IProps {
    showPopup: boolean;
    size?: EPopupSizes;
    title: React.ReactNode;
    steps: {
        title?: string;
        content: React.ReactNode;
        name?: string;
        nextStepButtonText?: string;
        showSteps?: boolean
    }[];
    isNextButtonDisabled?: boolean;
    confirmFinished?: boolean;
    sendButtonText?: React.ReactNode;
    lastButtonText?: React.ReactNode;
    outsideScrollContent?: React.ReactNode;
    showDeclareNew?: boolean;
    wizardType?: string;
    isFormValid?: boolean;
    dirty?: boolean;
    sendButtonType?: 'button' | 'submit' | 'reset';
    contextMenu?: React.ReactNode;
    noPadding?: boolean;
    loadingData?: boolean;
    preventClosingOnClickingOutside?: boolean;
    outsideScrollTop?: number;
    customFooter?: React.ReactNode;
    loadingStepConfig?: ILoadingStepConfig;
    confirmHandler?();
    finishHandler?();
    clearForm?();
    onBeforeNextStep?(): Promise<boolean> | boolean;
}

export const StepsPopup: FC<React.PropsWithChildren<IProps>> = props => {
    const { intlFormatMessage } = useIntlMessage();
    const nextButtonDisabled =
        props.isNextButtonDisabled !== undefined
            ? props.isNextButtonDisabled
            : !(props.isFormValid && props.dirty);
    const [wrapperRef, dropdownOpen, toggleDropdown, closeDropdown] = useDropdown();
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const popupWrapper = useRef<HTMLDivElement>();
    const navigating = useRef(false);
    let scrollBarRef = useRef<any>(undefined);
    const [outsideScrollContentHeight, setOutsideScrollContentHeight] = useState(undefined);
    const [showCloseInfo, setShowCloseInfo] = useState<boolean>(false);
    const [showLoadingStep, setShowLoadingStep] = useState<boolean>(false);
    const [stepChanging, setStepChanging] = useState<boolean>(false);
    // tslint:disable-next-line:no-null-keyword
    const outsideContentRef = useRef<HTMLDivElement>(null);
    // tslint:disable-next-line:no-null-keyword
    const stepsContentRef = useRef<HTMLDivElement>(null);
    const hasLoadingStep = !!props.loadingStepConfig?.texts?.length && (props.loadingStepConfig?.intervalMs > 0);
    const isLoadingStepVisible = hasLoadingStep && showLoadingStep && (loading || !!props.loadingData);

    useEscKey(() => setShowCloseInfo(true));

    useEffect(() => {
        if (props.outsideScrollTop) {
            scrollBarRef.current?.scrollTop(props.outsideScrollTop);
        }
    }, [props.outsideScrollTop]);

    useEffect(() => {
        if (props.showPopup) {
            setCurrentStep(1);
            setShowLoadingStep(false);
        }
        setShowPopup(props.showPopup);
    }, [props.showPopup]);

    useEffect(() => {
        if (hasLoadingStep && (loading || !!props.loadingData)) {
            setShowLoadingStep(true);
        }
    }, [hasLoadingStep, loading, props.loadingData]);

    useEffect(() => {
        if (showLoadingStep && !loading && !props.loadingData) {
            setShowLoadingStep(false);
        }
    }, [showLoadingStep, loading, props.loadingData]);

    useEffect(() => {
        if (!props.preventClosingOnClickingOutside) {
            const handleClickOutside = (event) => {
                if (loading || props.loadingData) {
                    return;
                }

                const target = event.target as Node;
                const element = target as Element;

                const isInsideNonClickArea = element.closest?.('[data-nonclick]');
                if (isInsideNonClickArea) {
                    return;
                }

                if (popupWrapper.current && !popupWrapper.current.contains(target)) {
                    props.finishHandler();
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [props.preventClosingOnClickingOutside, props.finishHandler, loading, props.loadingData]);

    useEffect(() => {
        setStepChanging(true);
        const timer = setTimeout(() => setStepChanging(false), 400);
        return () => clearTimeout(timer);
    }, [currentStep]);

    const handleScrollbarsMount = useCallback((scrollbar) => {
        scrollBarRef.current = scrollbar;
        scrollbar?.scrollToTop();
    }, [currentStep]);

    useEffect(() => {
        if (!showPopup || typeof ResizeObserver === 'undefined') {
            return;
        }
        const node = stepsContentRef.current;
        if (!node) {
            return;
        }
        const observer = new ResizeObserver(() => {
            const view: HTMLElement | undefined = scrollBarRef.current?.view;
            if (!view) {
                return;
            }
            const top = view.scrollTop;
            view.scrollTop = top + 1;
            view.scrollTop = top;
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, [showPopup]);

    const addStep = useCallback(() => {
        setCurrentStep(step => step + 1);
    }, []);

    const handleNextClick = useCallback(async () => {
        if (navigating.current) {
            return;
        }
        navigating.current = true;
        try {
            if (props.onBeforeNextStep) {
                const canAdvance = await Promise.resolve(props.onBeforeNextStep());
                if (canAdvance) {
                    addStep();
                }
            } else {
                addStep();
            }
        } finally {
            navigating.current = false;
        }
    }, [props.onBeforeNextStep, addStep]);

    useEffect(() => {
        props.confirmFinished && addStep();
    }, [props.confirmFinished]);

    const substractStep = useCallback(() => {
        setCurrentStep(step => step - 1);
    }, []);

    const confirm = async () => {
        if (props.onBeforeNextStep) {
            const canProceed = await Promise.resolve(props.onBeforeNextStep());
            if (!canProceed) return;
        }
        if (props.confirmHandler) {
            setLoading(true);
                if (hasLoadingStep) {
                    setShowLoadingStep(true);
                }
            try {
                const ret = props.confirmHandler();
                const isPromiseLike = ret && typeof ret.then === 'function';
                if (isPromiseLike) {
                    Promise.resolve(ret).finally(() => {
                        setLoading(false);
                    });
                } else {
                    setLoading(false);
                }
            } catch (error) {
                setLoading(false);
            }
        }
    };

    const finish = useCallback(() => {
        setLoading(false);
        props.finishHandler();
    }, [props.finishHandler]);

    const declareNew = useCallback(() => {
        props.clearForm();
        setCurrentStep(1);
    }, [currentStep]);

    const openContextMenu = useCallback(() => {
        toggleDropdown();
    }, [toggleDropdown]);

useEffect(() => {
    if (outsideContentRef?.current?.offsetHeight > 0) {
        setOutsideScrollContentHeight(outsideContentRef?.current?.offsetHeight);
    } else {
        setOutsideScrollContentHeight(0);
    }
}, [props.outsideScrollContent]);

    return (
        <>
            {showPopup &&
                <SWrapper>
                    <SWrapperInner size={props.size || EPopupSizes.SMALL} ref={popupWrapper}>
                        <SHeader>
                            <STitle>{props.title}</STitle>
                            <RightMenuWrapper>
                                {props.contextMenu &&
                                    <ContextMenuWrapper ref={wrapperRef} onClick={openContextMenu}>
                                        <FontAwesomeIcon icon={faEllipsisH} />
                                        {dropdownOpen &&
                                            <>
                                                {props.contextMenu}
                                            </>
                                        }
                                    </ContextMenuWrapper>
                                }
                                <SClosePopup onClick={(loading || props.loadingData) ? undefined : props.finishHandler}>
                                    <FontAwesomeIcon icon={faXmark} color={colorStack.darkGrey}/>
                                </SClosePopup>
                            </RightMenuWrapper>
                        </SHeader>
                        <SContent>
                            <SStepsContentWrapper isHidden={isLoadingStepVisible}>
                                <Scrollbars
                                    autoHeight
                                    autoHeightMin={`calc(60vh - ${props.outsideScrollContent ? outsideScrollContentHeight : 0}px)`}
                                    autoHeightMax={`calc(60vh - ${props.outsideScrollContent ? outsideScrollContentHeight : 0}px)`}
                                    ref={handleScrollbarsMount}
                                    renderView={viewProps => <SScrollbarView {...viewProps} />}
                                >
                                    <div ref={stepsContentRef}>
                                        {props.steps.map((step, index) =>
                                            <SingleStep
                                                key={index}
                                                data={({
                                                    title: step.title,
                                                    name: step.showSteps && (step.name || `${intlFormatMessage({ id: 'global.step' }, { index: index.toString() })}`)
                                                })}
                                                isActive={currentStep - 1 === index}
                                                noPadding={props.noPadding}
                                            >
                                                {step.content}
                                            </SingleStep>
                                        )}
                                    </div>
                                </Scrollbars>
                                {props.outsideScrollContent && (
                                    <OustideContentWrapper isHidden={outsideScrollContentHeight === 0} ref={outsideContentRef}>{props.outsideScrollContent}</OustideContentWrapper>
                                )}
                            </SStepsContentWrapper>
                            {isLoadingStepVisible && (
                                <SLoadingStepOverlay>
                                    <LoadingStep
                                        isVisible={isLoadingStepVisible}
                                        intervalMs={props.loadingStepConfig.intervalMs}
                                        texts={props.loadingStepConfig.texts}
                                    />
                                </SLoadingStepOverlay>
                            )}
                        </SContent>
                        {!isLoadingStepVisible && (props.customFooter !== undefined
                            ? props.customFooter
                            : (
                            <SFooter>
                                <SSteps>
                                    {props?.steps?.length > 2 && currentStep !== props.steps?.length && (
                                        <>
                                            <IntlMessage id="cluster.stepsPopup.steps" values={{ currentStep: currentStep, stepsLength: props.steps?.length - 1 }} />
                                            <ProgressBar percent={(currentStep / (props.steps?.length - 1)) * 100} />
                                        </>
                                    )}
                                </SSteps>
                                <SButtonBox>
                                    {(currentStep > 1 && currentStep !== props.steps.length) &&
                                    <Button onClick={substractStep} disabled={loading || props.loadingData} typeSchema={EButtonTypeSchema.TERTIARY} data-lc="js-lc-button-back"><IntlMessage id="global.back" /></Button>
                                }
                                    {currentStep < props.steps.length - 1 &&
                                    <Button
                                        onClick={handleNextClick}
                                        disabled={loading || props.loadingData || stepChanging || (props.onBeforeNextStep ? false : nextButtonDisabled)}
                                        data-lc="js-lc-button-next"
                                    >
                                        <IntlMessage id="global.next" />
                                    </Button>
                                }
                                    {currentStep === props.steps.length && (
                                    <>
                                        {props.showDeclareNew && (
                                            <Button onClick={declareNew} typeSchema={EButtonTypeSchema.TERTIARY} data-lc="js-lc-button-anotherDeclaration">
                                                {props.wizardType === ETemplateTypes.COI && <IntlMessage id="coi.declareAnotherLink" />}
                                                {props.wizardType === ETemplateTypes.Gift && <IntlMessage id="giftinvitation.startAnotherDeclaration" />}
                                                {props.wizardType === ETemplateTypes.TradeAssociations && <IntlMessage id="tradeAssociation.wizard.declareNew" />}
                                            </Button>
                                        )}
                                        <Button typeSchema={EButtonTypeSchema.TERTIARY} data-lc="js-lc-button-close" onClick={finish} disabled={loading || props.loadingData}>{props.lastButtonText || <IntlMessage id="global.close" />}</Button>
                                    </>
                                )}
                                    {(currentStep === props.steps.length - 1 || (props.confirmHandler && props.steps.length === 1)) &&
                                    <>
                                        <SpinnerWrapper>
                                            <Button
                                                onClick={confirm}
                                                data-lc="js-lc-button-send"
                                                type={props.sendButtonType || 'submit'}
                                                disabled={loading || props.loadingData || (props.isFormValid !== undefined && !props.isFormValid)}
                                                loading={loading || props.loadingData}
                                            >
                                                {props.sendButtonText || <IntlMessage id="global.send" />}
                                            </Button>
                                        </SpinnerWrapper>
                                    </>
                                }
                                </SButtonBox>
                            </SFooter>
                        ))}
                    </SWrapperInner>
                </SWrapper>
            }
            <ConfirmPopup
                showPopup={showCloseInfo}
                title={intlFormatMessage({ id: 'global.stepsPopup.closePopup.title' })}
                size={EPopupSizes.SMALL}
                yesHandler={() => {
                    setShowCloseInfo(false);
                    props.finishHandler();
                }}
                noHandler={() => setShowCloseInfo(false)}
                loading={loading}
            >
                {intlFormatMessage({ id: 'global.stepsPopup.closePopup.content' })}
            </ConfirmPopup>
        </>
    );
};
